<template>
  <div class="d-flex flex-column flex-column-fluid">
    <!-- Toolbar -->
    <div id="kt_app_toolbar" class="app-toolbar py-3 py-lg-6">
      <div
        id="kt_app_toolbar_container"
        class="app-container container-fluid d-flex flex-stack"
      >
        <div
          class="page-title d-flex flex-column justify-content-center flex-wrap me-3"
        >
          <h1 class="page-heading text-dark fw-bold fs-3 my-0">
            {{ $t("sch.index.title") }}
          </h1>
          <ul
            class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1"
          >
            <li class="breadcrumb-item text-muted">
              <a href="#" class="text-muted text-hover-success">{{
                $t("common.home")
              }}</a>
            </li>
            <li class="breadcrumb-item">
              <span class="bullet bg-gray-400 w-5px h-2px"></span>
            </li>
            <li class="breadcrumb-item text-muted">{{ $t("common.home") }}</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div id="kt_app_content" class="app-content flex-column-fluid">
      <div id="kt_app_content_container" class="app-container container-fluid">
        <div v-if="isloading" class="skeleton-wrap">
          <div class="row g-3">
            <div
              class="col-12 col-sm-6 col-md-4 col-lg-3"
              v-for="n in 8"
              :key="n"
            >
              <div class="skeleton-card">
                <div class="skeleton-img"></div>
                <div class="skeleton-line w-75 mt-3"></div>
                <div class="d-flex gap-2 mt-2">
                  <div class="skeleton-chip w-50"></div>
                  <div class="skeleton-chip w-25"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="col-md-12">
          <!-- Banners -->
          <bannersHome />

          <!-- Scholarships Section -->
          <div class="scholarship-section mt-5" :class="{ 'scholarship-section--rtl': isRtl, 'scholarship-section--ltr': !isRtl }">
            <div
              class="d-flex justify-content-between align-items-center mb-3 section-header"
            >
              <h3
                class="fw-bold text-dark mb-0"
                style="font-weight: 650 !important"
              >
                <i class="fa fa-graduation-cap section-icon"></i>
                {{ $t("sch.index.may_interest") }}
              </h3>
              <div class="d-flex gap-2">
                <router-link
                  to="/scholarships-favorites"
                  class="btn btn-sm btn-pill section-btn section-btn--favorites"
                >
                  <i class="fa fa-heart me-1"></i>
                  {{ $t("favorites.title") || "المفضلة" }}
                </router-link>
                <button
                  class="btn btn-sm btn-pill section-btn section-btn--viewall"
                  @click="viewAllScholarships"
                >
                  {{ $t("common.view_all") }}
                  <i :class="['fa', isRtl ? 'fa-arrow-left' : 'fa-arrow-right', isRtl ? 'ms-1' : 'ms-1', 'icon-shift']"></i>
                </button>
              </div>
            </div>

            <!-- Swiper -->
            <Swiper
              v-if="displayedScholarships.length"
              :modules="[Pagination, Autoplay]"
              :loop="true"
              :autoplay="{ delay: 2500, disableOnInteraction: false }"
              :pagination="{ clickable: true }"
              :breakpoints="{
                320: { slidesPerView: 1, spaceBetween: 10 },
                576: { slidesPerView: 2, spaceBetween: 10 },
                768: { slidesPerView: 3, spaceBetween: 12 },
                992: { slidesPerView: 4, spaceBetween: 15 },
              }"
              :dir="isRtl ? 'rtl' : 'ltr'"
              class="mySwiper"
            >
              <SwiperSlide
                v-for="scholarship in displayedScholarships"
                :key="scholarship.id"
                @click="goToScholarship(scholarship.id)"
              >
                <div class="scholarship-card text-center">
                  <img
                    :src="getScholarshipImage(scholarship)"
                    class="scholarship-img"
                    @error="handleImageError"
                  />
                  <h6 class="mt-3 fw-semibold scholarship-title">
                    {{ scholarship.name_ar || scholarship.name }}
                  </h6>
                  <div
                    class="sch-meta d-flex justify-content-center gap-2 flex-wrap mt-2"
                  >
                    <span v-if="scholarship.city" class="chip"
                      ><i class="fa fa-map-marker-alt me-1"></i
                      >{{ scholarship.city }}</span
                    >
                    <span
                      v-if="scholarship.funding_type"
                      class="chip chip-outline"
                      ><i class="fa fa-wallet me-1"></i
                      >{{ scholarship.funding_type }}</span
                    >
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          <!-- Events -->
          <eventsHome />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useI18n } from "vue-i18n";

import bannersHome from "./banners.vue";
import eventsHome from "./events.vue";

import { apiUrl } from "@/utils/api";

const { locale } = useI18n();
const isRtl = computed(() => locale.value === "ar");

const scholarships = ref([]);
const isloading = ref(true);
const defaultImage = "/assets/img/acad-icon.png";

const displayedScholarships = computed(() => scholarships.value.slice(0, 8));

const handleImageError = (e) => {
  e.target.src = defaultImage;
};

const getScholarshipImage = (scholarship) => {
  if (
    scholarship.banners &&
    scholarship.banners.length > 0 &&
    scholarship.banners[0].image_url
  ) {
    return scholarship.banners[0].image_url;
  }
  if (scholarship.image) {
    return scholarship.image.startsWith("http")
      ? scholarship.image
      : `${apiUrl}${scholarship.image}`;
  }
  return defaultImage;
};

const viewAllScholarships = () => {
  window.location.href = "/scholarships-all";
};

const goToScholarship = (id) => {
  window.location.href = `/scholarship-details?id=${id}`;
};

const fetchScholarships = async () => {
  try {
    const response = await fetch(`${apiUrl}/scholarships`, {
      headers: { "x-country-code": window.appRegion.phone_code },
    });
    const json = await response.json();
    scholarships.value = json.data || [];
  } catch (err) {
    console.error("Error fetching scholarships:", err);
  } finally {
    isloading.value = false;
  }
};

onMounted(fetchScholarships);
</script>

<style scoped>
/* RTL/LTR Support */
.scholarship-section--rtl {
  direction: rtl;
}
.scholarship-section--ltr {
  direction: ltr;
}

/* Swiper */
.mySwiper {
  padding-bottom: 50px;
}
:deep(.swiper-pagination-bullet) {
  background: #bfeecf;
  opacity: 1;
  width: 10px;
  height: 10px;
  transition: all 0.3s ease;
}
:deep(.swiper-pagination-bullet.swiper-pagination-bullet-active) {
  background: linear-gradient(135deg, #00c444 0%, #00e050 100%);
  width: 28px;
  border-radius: 6px;
}
:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  color: #00c444;
}

/* Section header gradient */
.section-header {
  background: linear-gradient(135deg, #f0fff5 0%, #e8fff1 50%, #ffffff 100%);
  border: 1px solid rgba(0, 196, 68, 0.15);
  border-radius: 20px;
  padding: 16px 22px;
  position: relative;
  overflow: hidden;
}
.section-header::before {
  content: "";
  position: absolute;
  top: 0;
  width: 120px;
  height: 120px;
  background: radial-gradient(
    circle,
    rgba(0, 196, 68, 0.08) 0%,
    transparent 70%
  );
  border-radius: 50%;
}
.scholarship-section--rtl .section-header::before {
  right: 0;
  left: auto;
  transform: translate(30%, -30%);
}
.scholarship-section--ltr .section-header::before {
  left: 0;
  right: auto;
  transform: translate(-30%, -30%);
}

/* Card */
.scholarship-card {
  border-radius: 20px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 196, 68, 0.03);
  position: relative;
  overflow: hidden;
}
.scholarship-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #00c444, #00e050, #00c444);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.scholarship-card:hover::before {
  opacity: 1;
  animation: gradientSlide 2s linear infinite;
}
@keyframes gradientSlide {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}
.scholarship-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 196, 68, 0.15), 0 8px 16px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 196, 68, 0.25);
}

.scholarship-img {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 14px;
  border: none;
  transition: all 0.4s ease;
  position: relative;
}
.scholarship-card::after {
  content: "";
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  height: 160px;
  border-radius: 14px;
  background: linear-gradient(180deg, transparent 60%, rgba(0, 0, 0, 0.4) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}
.scholarship-card:hover::after {
  opacity: 1;
}
.scholarship-card:hover .scholarship-img {
  transform: scale(1.08);
  filter: brightness(1.05);
}

.scholarship-title {
  font-size: 15px;
  line-height: 1.5;
  color: #1a1a2e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 45px;
  font-weight: 600;
  transition: color 0.3s ease;
}
.scholarship-card:hover .scholarship-title {
  color: #00a43c;
}

/* Meta chips */
.chip {
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e2 100%);
  color: #0f5132;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid rgba(0, 196, 68, 0.2);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.chip:hover {
  background: linear-gradient(135deg, #d4f7e2 0%, #c6ffda 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.15);
}
.chip i {
  font-size: 10px;
  color: #00c444;
}
.chip-outline {
  background: rgba(255, 255, 255, 0.9);
  color: #00a43c;
  border: 1px solid rgba(0, 196, 68, 0.3);
  backdrop-filter: blur(4px);
}
.chip-outline:hover {
  background: #00c444;
  color: #fff;
  border-color: #00c444;
}
.chip-outline:hover i {
  color: #fff;
}

/* Button */
.btn-success {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.3);
  transition: all 0.3s ease;
}
.btn-success:hover {
  background: linear-gradient(135deg, #00d44a 0%, #00b844 100%) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
}
.btn-outline-success {
  border: 2px solid #00c444 !important;
  color: #00c444 !important;
  background: transparent;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.btn-outline-success::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 196, 68, 0.1),
    transparent
  );
  transition: left 0.5s ease;
}
.btn-outline-success:hover::before {
  left: 100%;
}
.btn-outline-success:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  color: #fff !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.3);
}
.btn-pill {
  border-radius: 999px;
  padding: 8px 20px;
}

/* ========== Unified Section Buttons ========== */
.section-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.875rem;
  color: #fff !important;
  border: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.section-btn i,
.section-btn .fa,
.section-btn .fas,
.section-btn .far {
  color: #fff !important;
}

.section-btn:hover {
  transform: translateY(-3px);
  color: #fff !important;
}

.section-btn:hover i,
.section-btn:hover .fa,
.section-btn:hover .fas,
.section-btn:hover .far {
  color: #fff !important;
}

/* Favorites Button - Red/Pink gradient */
.section-btn--favorites {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
  box-shadow: 0 4px 15px rgba(238, 90, 90, 0.35);
}

.section-btn--favorites:hover {
  background: linear-gradient(135deg, #ff5252 0%, #e53935 100%);
  box-shadow: 0 8px 25px rgba(238, 90, 90, 0.45);
}

/* View All Button - Green gradient */
.section-btn--viewall {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%);
  box-shadow: 0 4px 15px rgba(0, 196, 68, 0.35);
}

.section-btn--viewall:hover {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.45);
}

/* View all icon hover animation */
.section-btn--viewall .icon-shift {
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.scholarship-section--rtl .section-btn--viewall:hover .icon-shift {
  transform: translateX(-6px);
}
.scholarship-section--ltr .section-btn--viewall:hover .icon-shift {
  transform: translateX(6px);
}

/* Skeletons */
.skeleton-wrap {
  padding: 8px 0 24px;
}
.skeleton-card {
  background: linear-gradient(145deg, #ffffff 0%, #fafffe 100%);
  border: 1px solid rgba(0, 196, 68, 0.08);
  border-radius: 20px;
  padding: 16px;
  overflow: hidden;
}
.skeleton-img {
  height: 160px;
  border-radius: 14px;
  background: linear-gradient(90deg, #f0f9f4 0%, #e0f5e8 50%, #f0f9f4 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}
.skeleton-line {
  height: 14px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f9f4 0%, #e0f5e8 50%, #f0f9f4 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}
.skeleton-chip {
  height: 24px;
  border-radius: 999px;
  background: linear-gradient(90deg, #f0f9f4 0%, #e0f5e8 50%, #f0f9f4 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Section heading polish */
.scholarship-section h3 {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.25rem;
}

/* Unified Section Icon Style */
.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e8fff1 0%, #c6ffda 100%);
  color: #00c444 !important;
  font-size: 16px;
  border: 2px solid rgba(0, 196, 68, 0.25);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.25);
  transition: all 0.3s ease;
}
.scholarship-section--rtl .section-icon {
  margin-left: 10px;
}
.scholarship-section--ltr .section-icon {
  margin-right: 10px;
}
.section-icon:hover {
  transform: scale(1.1) rotate(-5deg);
  box-shadow: 0 8px 25px rgba(0, 196, 68, 0.35);
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
.scholarship-section .d-flex.justify-content-between {
  align-items: center;
}

/* Image container overflow */
.scholarship-card > div:first-child,
.scholarship-card {
  overflow: hidden;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .scholarship-card {
    padding: 12px;
  }
  .scholarship-img {
    height: 140px;
  }
  .section-header {
    padding: 12px 16px;
  }
}

/* ========== DARK MODE ========== */
[data-bs-theme="dark"] .scholarship-section,
[data-theme="dark"] .scholarship-section,
.dark .scholarship-section {
  color: #e4e6eb;
}

[data-bs-theme="dark"] .section-header,
[data-theme="dark"] .section-header,
.dark .section-header {
  background: linear-gradient(135deg, #1a2e23 0%, #1e3a2c 50%, #242526 100%);
  border-color: rgba(0, 196, 68, 0.25);
}
[data-bs-theme="dark"] .section-header::before,
[data-theme="dark"] .section-header::before,
.dark .section-header::before {
  background: radial-gradient(
    circle,
    rgba(0, 196, 68, 0.15) 0%,
    transparent 70%
  );
}

[data-bs-theme="dark"] .scholarship-card,
[data-theme="dark"] .scholarship-card,
.dark .scholarship-card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.15);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 196, 68, 0.05);
}
[data-bs-theme="dark"] .scholarship-card:hover,
[data-theme="dark"] .scholarship-card:hover,
.dark .scholarship-card:hover {
  box-shadow: 0 20px 40px rgba(0, 196, 68, 0.2), 0 8px 16px rgba(0, 0, 0, 0.4);
  border-color: rgba(0, 196, 68, 0.35);
}

[data-bs-theme="dark"] .scholarship-title,
[data-theme="dark"] .scholarship-title,
.dark .scholarship-title {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .scholarship-card:hover .scholarship-title,
[data-theme="dark"] .scholarship-card:hover .scholarship-title,
.dark .scholarship-card:hover .scholarship-title {
  color: #00e050;
}

[data-bs-theme="dark"] .chip,
[data-theme="dark"] .chip,
.dark .chip {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  color: #8bffb3;
  border-color: rgba(0, 196, 68, 0.35);
}
[data-bs-theme="dark"] .chip:hover,
[data-theme="dark"] .chip:hover,
.dark .chip:hover {
  background: linear-gradient(135deg, #1e4d35 0%, #237a4d 100%);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}
[data-bs-theme="dark"] .chip i,
[data-theme="dark"] .chip i,
.dark .chip i {
  color: #00e050;
}

[data-bs-theme="dark"] .chip-outline,
[data-theme="dark"] .chip-outline,
.dark .chip-outline {
  background: rgba(36, 37, 38, 0.9);
  color: #00e050;
  border-color: rgba(0, 196, 68, 0.4);
}
[data-bs-theme="dark"] .chip-outline:hover,
[data-theme="dark"] .chip-outline:hover,
.dark .chip-outline:hover {
  background: #00c444;
  color: #fff;
}

[data-bs-theme="dark"] .skeleton-card,
[data-theme="dark"] .skeleton-card,
.dark .skeleton-card {
  background: linear-gradient(145deg, #242526 0%, #1a1b1d 100%);
  border-color: rgba(0, 196, 68, 0.1);
}
[data-bs-theme="dark"] .skeleton-img,
[data-bs-theme="dark"] .skeleton-line,
[data-bs-theme="dark"] .skeleton-chip,
[data-theme="dark"] .skeleton-img,
[data-theme="dark"] .skeleton-line,
[data-theme="dark"] .skeleton-chip,
.dark .skeleton-img,
.dark .skeleton-line,
.dark .skeleton-chip {
  background: linear-gradient(90deg, #2d2e30 0%, #3a3b3d 50%, #2d2e30 100%);
  background-size: 200% 100%;
}

[data-bs-theme="dark"] .scholarship-section h3,
[data-theme="dark"] .scholarship-section h3,
.dark .scholarship-section h3 {
  color: #e4e6eb;
}
[data-bs-theme="dark"] .section-icon,
[data-theme="dark"] .section-icon,
.dark .section-icon {
  background: linear-gradient(135deg, #1a3d2a 0%, #1e4d35 100%);
  border-color: rgba(0, 196, 68, 0.4);
  color: #00e050 !important;
  box-shadow: 0 8px 20px rgba(0, 196, 68, 0.35);
}

[data-bs-theme="dark"] .btn-outline-success,
[data-theme="dark"] .btn-outline-success,
.dark .btn-outline-success {
  border-color: #00e050 !important;
  color: #00e050 !important;
}
[data-bs-theme="dark"] .btn-outline-success:hover,
[data-theme="dark"] .btn-outline-success:hover,
.dark .btn-outline-success:hover {
  background: linear-gradient(135deg, #00c444 0%, #00a43c 100%) !important;
  color: #fff !important;
}

[data-bs-theme="dark"] :deep(.swiper-pagination-bullet),
[data-theme="dark"] :deep(.swiper-pagination-bullet),
.dark :deep(.swiper-pagination-bullet) {
  background: rgba(0, 196, 68, 0.4);
}
[data-bs-theme="dark"] :deep(.swiper-pagination-bullet-active),
[data-theme="dark"] :deep(.swiper-pagination-bullet-active),
.dark :deep(.swiper-pagination-bullet-active) {
  background: linear-gradient(135deg, #00e050 0%, #00c444 100%);
}
</style>
